import React, { useState, useEffect } from 'react';
import {
    Star, MessageCircle, Users, Search,
    ChevronRight, ChevronDown, User, Bell, Heart, Share2,
    Shield, Verified, MapPin, Calendar, Clock, DollarSign,
    Mail, Instagram, Globe, Bookmark,
    CheckCircle, Info,
    Target, Trophy, Crown,
    PlusCircle, Edit3, Camera,
    BarChart3, Activity, X, LogOut,
    Loader, AlertTriangle, Copy
} from 'lucide-react';
import { API_BASE_URL } from '../constants';
import { serviceTypes, categories, regions, reviewTypes, mockServices } from '../constants';

import { EnhancedDropdown } from './EnhancedDropdown';
import ProfileImage from './ProfileImage';

function formatFollowers(num) {
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    return num;
}


function MyReviewsPage({
    isLoggedIn,
    isVerified,
    userReviews,
    setShowQuickReview,
}) {

    const [viewMode, setViewMode] = useState('grid'); // grid, list, map
    const [userReviewsToShow, setUserReviewsToShow] = React.useState(6);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section for User Reviews */}
            <section className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-5xl font-bold mb-6 leading-tight">
                            Your Reviews
                            <span className="block bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                                Your contributions to the community
                            </span>
                        </h1>
                        <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
                            Here you can view, manage, and celebrate all the reviews you've shared. Your feedback helps others make informed decisions and builds trust in our community.
                        </p>
                    </div>
                </div>
            </section>


            {/* User Reviews Section */}
            {(isLoggedIn || isVerified) && (
                <section className="py-8 bg-gradient-to-r from-blue-50 to-purple-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-left mb-8">
                            <p className="text-gray-600">
                                {userReviews && userReviews.length > 0
                                    ? `You have ${userReviews.length} review${userReviews.length !== 1 ? 's' : ''}`
                                    : 'Start sharing your experiences with the community'
                                }
                            </p>
                        </div>

                        {userReviews && userReviews.length > 0 ? (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {userReviews.slice(0, userReviewsToShow).map((review, index) => {

                                        // Check each field for objects
                                        Object.keys(review).forEach(key => {
                                            const value = review[key];
                                            // if (typeof value === 'object' && value !== null) {
                                            //   console.log(`🚨 Object field found: ${key}`, value);
                                            // }
                                        });

                                        return (

                                            <div key={review.reviewId || review.id || review._id} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">

                                                {/* Header with Business Info */}
                                                < div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
                                                    <div className="flex items-center justify-between">

                                                        <div>
                                                            <h3 className="font-bold text-lg truncate">
                                                                {(() => {
                                                                    const businessUsername = review.targetBusiness?.username ||
                                                                        review.targetBusinessUsername ||
                                                                        review.business;

                                                                    const businessFullname = review.targetBusiness?.full_name

                                                                    // If business name is 'unknown' or empty, try to extract from other fields
                                                                    if (!businessUsername || businessUsername === 'unknown') {
                                                                        // Try to get from reviewer identifier as fallback
                                                                        const reviewerIdentifier = review.reviewer?.identifier ||
                                                                            review.reviewer?.legacyIdentifier;

                                                                        if (reviewerIdentifier && reviewerIdentifier !== 'anonymous' && !reviewerIdentifier.startsWith('anon_')) {
                                                                            // Clean the identifier and add single @
                                                                            const cleanIdentifier = reviewerIdentifier.replace(/^@+/, '');
                                                                            return `@${cleanIdentifier}`;
                                                                        }

                                                                        return 'Business Review';
                                                                    }

                                                                    // Clean the username and add single @
                                                                    const cleanUsername = businessUsername.replace(/^@+/, '');
                                                                    return `${cleanUsername}`;
                                                                })()}
                                                            </h3>
                                                            <span>{review.targetBusiness?.full_name}</span>
                                                            <p className="text-purple-100 text-sm">
                                                                {(() => {
                                                                    const displayName = review.targetBusiness?.displayName ||
                                                                        review.businessName;

                                                                    // Don't show anything if it's unknown, empty, or doesn't exist
                                                                    if (!displayName || displayName === 'unknown' || displayName === 'Instagram Business') {
                                                                        return '';
                                                                    }

                                                                    return String(displayName);
                                                                })()}
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-yellow-200 text-sm">
                                                                {(() => {
                                                                    const rating = review.rating;
                                                                    const ratingValue = typeof rating === 'object' && rating?.overall ? rating.overall : (rating || 0);
                                                                    return '★'.repeat(ratingValue) + '☆'.repeat(5 - ratingValue);
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Review Content */}
                                                <div className="p-4">
                                                    {/* Service Type & Category */}
                                                    <div className="flex items-center space-x-2 mb-3">
                                                        <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
                                                            {(() => {
                                                                const serviceType = review.serviceType || review.serviceDetails?.serviceType;
                                                                if (typeof serviceType === 'object' && serviceType?.name) {
                                                                    return String(serviceType.name);
                                                                }
                                                                return String(serviceType || 'General Service');
                                                            })()}
                                                        </div>
                                                        {review.reviewType && (
                                                            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                                                                {(() => {
                                                                    const reviewType = review.reviewType;
                                                                    if (typeof reviewType === 'object' && reviewType?.name) {
                                                                        return String(reviewType.name);
                                                                    }
                                                                    return String(reviewType);
                                                                })()}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Review Text */}
                                                    <div className="mb-4">
                                                        <p className="text-gray-700 text-sm leading-relaxed line-clamp-3">
                                                            {(() => {
                                                                const comment = review.comment || review.content?.comment || review.text;
                                                                return String(comment || 'No comment provided');
                                                            })()}
                                                        </p>
                                                    </div>

                                                    {/* Footer with Details */}
                                                    <div className="border-t pt-3 space-y-2">
                                                        {/* Verification Status */}
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center space-x-2">
                                                                {review.verification?.isVerified || review.verified ? (
                                                                    <>
                                                                        <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                                                            <span className="text-white text-xs">✓</span>
                                                                        </div>
                                                                        <span className="text-green-600 text-xs font-medium">Verified Review</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center">
                                                                            <span className="text-white text-xs">?</span>
                                                                        </div>
                                                                        <span className="text-gray-500 text-xs">Anonymous Review</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <span className="text-gray-400 text-xs">
                                                                {review.metadata?.completedAt ? new Date(review.metadata.completedAt).toLocaleDateString() :
                                                                    review.createdAt ? new Date(review.createdAt).toLocaleDateString() :
                                                                        'Recent'}
                                                            </span>
                                                        </div>

                                                        {/* Region & Verification Method */}
                                                        <div className="flex items-center justify-between text-xs text-gray-500">
                                                            <span>
                                                                📍 {(() => {
                                                                    const region = review.region || review.location?.region;
                                                                    if (typeof region === 'object' && region?.name) {
                                                                        return String(region.name);
                                                                    }
                                                                    return String(region || 'Global');
                                                                })()}
                                                            </span>
                                                            {(review.verification?.method || review.verificationMethod) && (
                                                                <span className="capitalize">
                                                                    via {String(review.verification?.method || review.verificationMethod)}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Promotion Code if exists */}
                                                        {review.promotionCode && String(review.promotionCode).trim() && (
                                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mt-2">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-yellow-600 text-xs">🎟️ Promotion Code:</span>
                                                                    <span className="text-yellow-800 text-xs font-mono font-medium">
                                                                        {(() => {
                                                                            try {
                                                                                const code = review.promotionCode;
                                                                                if (typeof code === 'string') return code;
                                                                                if (typeof code === 'object') return JSON.stringify(code);
                                                                                return String(code);
                                                                            } catch (error) {
                                                                                console.error('Error rendering promotion code:', error);
                                                                                return 'CODE_ERROR';
                                                                            }
                                                                        })()}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {userReviewsToShow < userReviews.length && (
                                    <div className="flex justify-center mt-6">
                                        <button
                                            onClick={() => setUserReviewsToShow(prev => prev + 6)}
                                            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-all"
                                        >
                                            Load More
                                        </button>
                                    </div>
                                )}
                            </>

                        ) : (
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-8 text-center max-w-md mx-auto">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <MessageCircle className="text-white" size={24} />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Reviews Yet</h3>
                                <p className="text-gray-600 mb-6">You haven't written any reviews yet. Start sharing your experiences with the IGReview community!</p>
                                <button
                                    onClick={() => setShowQuickReview(true)}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all"
                                >
                                    <PlusCircle size={20} />
                                    Write Your First Review
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            )
            }
        </div >
    );
}


export default MyReviewsPage;
